import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Trans, useTranslation } from 'react-i18next'
import { MakeAPaymentFormData, createSchema } from './schema'
import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { Alert, Tooltip } from '@cmsgov/design-system'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { PaymentMethod } from '@cms-max/medicare-api-sdk'
import LoadingButton from 'components/LoadingButton'
import { useEvent } from 'hooks/useEvent'

type MakeAPaymentFormProps = {
  onSubmit: (data: MakeAPaymentFormData) => void
  isSubmitting?: boolean
}
export function MakeAPaymentForm ({ isSubmitting, onSubmit }: MakeAPaymentFormProps) {
  const { t } = useTranslation('makeAPaymentPage', { keyPrefix: 'payYourPremium.form' })

  const schema = useMemo(() => createSchema(t), [t])
  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(schema)
  })

  const { watch, setValue } = methods

  const watchAmount = watch('amount')
  const isHighAmount = watchAmount && Number(watchAmount) > 3000

  const { fireEvent } = useEvent('premiumsMakeAPayment')

  const ensureDecimals = () => {
    // If user has entered something other than a number, don't do anything; we will display validation message
    if (isNaN(watchAmount)) {
      return
    }

    if (watchAmount && !/^\d+(\.\d{2})$/.test(watchAmount.toString())) {
      setValue('amount', Number(watchAmount).toFixed(2))
    }
  }

  return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <p>{t('allRequired')}</p>
          <MyChoiceList
            variableInputName="paymentMethod"
            label={t('paymentMethod.label')}
            labelClassName='ds-u-text-transform--uppercase'
            errorMessageString=""
            addErrorPrefix
            choices={[
              {
                label: t('paymentMethod.card'),
                value: PaymentMethod.Cc
              },
              {
                label: t('paymentMethod.account'),
                value: PaymentMethod.Ach
              },
              {
                label: t('paymentMethod.hsa'),
                value: PaymentMethod.Hsa
              }
            ]}
          />
          <div>
            <Tooltip
              className="ds-c-tooltip__trigger-link"
              component="a"
              onOpen={() => fireEvent('premiums_makeapayment_whatsanhsa_hover')}
              title={t('hsaTooltip.content')}
            >
              {t('hsaTooltip.trigger')}
            </Tooltip>
          </div>

          <MyTextField
            name="amount"
            label={t('amount.label')}
            hint={t('amount.hint')}
            placeholder='0.00'
            addErrorPrefix
            autoComplete='off'
            onBlur={ensureDecimals}
          />

          {isHighAmount && (
            <Alert className="ds-u-margin-top--3 ds-u-measure--base" variation='warn' hideIcon>{t('largeAmountWarning')}</Alert>
          )}

          <p className='ds-u-margin-top--3 ds-u-measure--wide'>{t('amountRequirements')}</p>

          <div className='ds-u-margin-top--3'>
            <LoadingButton
              type='submit'
              className='ds-c-button ds-c-button--solid'
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={() => fireEvent('premiums_makeapayment_paythroughpaygov_click')}>
              {t('submit')}
            </LoadingButton>
          </div>

          <p className='ds-u-measure--wide ds-u-margin-top--3'>
            <Trans t={t} i18nKey='toFinish' components={{
              nowrap: <span className='my-u-whitespace-nowrap' />
            }} />
          </p>
        </form>
      </FormProvider>
  )
}

export default MakeAPaymentForm
